// src/pages/AccessControlMatrixPage.tsx
//
// ★★ ANNEX A STORY 17 — the access-control matrix, on screen for the first time.
//
// ══ WHAT THIS REPLACES ══
//
// Two download links on the audit page. `docs/delivery/DEMO-READINESS-PLAN.md`
// listed the story among the five surfaces that "open but do less than their
// story": *the access-control matrix has no user axis (17)*. The report behind
// those links rendered one row per role with THREE hand-picked capability columns
// out of the 39 `packages/auth` defines, and answered none of the story's other two
// criteria — no filtering, and no detection of orphaned accounts, excessive
// privileges, permission conflicts or inconsistent role assignments.
//
// ══ THE THREE TABLES, AND WHY THEY ARE ON ONE PAGE ══
//
// One fetch, three tables. The axes must describe ONE moment: a page that fetched
// findings and accounts separately could show a finding about an account the
// account table no longer lists, and an access-control review is exactly the task
// where that inconsistency would be believed.
//
// ══ ⚠ WHAT THIS SCREEN MUST NOT LET A READER ASSUME ══
//
//   * **A finding is not a defect.** Several are legitimate in a real deployment —
//     a central role holding grants over other agencies, an administrative account
//     with no bill grant. Every finding therefore carries the EVIDENCE it was
//     decided from, and the severities are named `Attention` and `Review` rather
//     than anything that reads as a verdict.
//   * **Zero findings from a detector is a RESULT.** Three of the five find nothing
//     on a healthy estate, so the tallies are rendered even when they are zero. A
//     screen that showed only findings would be indistinguishable from one whose
//     detectors never ran.
//   * **Two of the story's filter axes cannot be honoured here**, and the reasons
//     come from the SERVER (`filtersNotAvailable`) rather than being written in
//     this file. An officer reads why, instead of finding out by being refused.
//
// ══ ★★ TWO PAGERS, AND WHY THE SORTS MOVED TO THE SERVER — 2026-08-20 ══
//
// The gateway now PAGES the two axes that grow with the estate (P2C3 §7.1.6 caps a
// payload at 50 KB and this report cost ~3 KB per account, so it refused above
// fourteen). Three consequences land on this screen and each one is a case in
// `AccessControlMatrixPage.dom.test.tsx`:
//
//   1. **The user axis and the findings axis get their OWN pager**, because the
//      server pages them independently — page one of the findings axis carries the
//      sharpest findings on the whole estate whatever page the account list is on.
//   2. **The sorts on those two tables became SERVER sorts.** This file used to say
//      a client comparator was complete "because this matrix arrives WHOLE or is
//      refused — there is no page to re-request in a different order". That stopped
//      being true, and a client sort over a page is exactly the defect `useSort.ts`
//      warns about: twenty of three hundred accounts re-ordered by role, reading as
//      "the accounts with the most authority". The CAPABILITY table keeps its client
//      comparator, because that axis is enumerated from `packages/auth` and really
//      does arrive whole.
//   3. **A tally is a WHOLE-ESTATE number and a row is a PAGE row**, and this screen
//      must never blur the two — the server's own `notes` say which is which, and
//      this page renders them verbatim as it already did.
import { humaniseCode, plainCode, plainProse } from '../lib/plain-words'
import { CategoryBadge, CodeBadge, RoleBadge, StateBadge } from '../components/StatusBadge'
import { useEffect, useMemo, useState } from 'react'
import { SgdsAlert, SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort } from '../lib/useSort'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { useClientSort, type ClientSortColumn, type ClientSortColumns } from '../lib/useClientSort'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { formatCents } from '../lib/format'
import type { MatrixCapability } from '../lib/types'

/** ⚠ The SERVER's own whitelist (`MATRIX_ACCOUNT_SORT_KEYS`) — an unknown key is a 400, not a default. */
type AccountSortKey = 'email' | 'role' | 'agencyCode' | 'grants' | 'active'
/** ⚠ The SERVER's own whitelist (`MATRIX_FINDING_SORT_KEYS`). */
type FindingSortKey = 'severity' | 'kind' | 'email' | 'role'

/*
  CENSUS-SERVER-SORT-HAND-BUILT: this page carries TWO independently paged and
  independently sorted axes in ONE response, and they cannot share `sortParams`.
  The accounts axis sends `sort`/`dir`; the findings axis sends `findingSort`/
  `findingDir`. `sortParams` emits the single pair `{ sort, dir }`, so using it for
  both would make re-ordering the findings silently re-order the accounts beside
  them — which is the defect the two separate parameter names exist to prevent. The
  query is therefore built by hand, from the same `useSort` state, immediately below.

  CENSUS-CLIENT-SORT-WITH-PAGER: the CAPABILITY matrix alone sorts in the browser.
  That axis is enumerated from `packages/auth` and arrives WHOLE — every capability
  the product defines, whatever the estate holds, with no limit, cursor or offset on
  it — so a client sort orders the list rather than a window into it. The accounts
  and findings tables on this same screen are offset-paged pages of the estate and
  sort on the SERVER; sorting one of those pages in the browser would look exactly
  like a working sort and would answer a different question.

  ⚠ The hand-rolled comparator this replaced was correct for its two columns and
  could not have grown to the third: it took `string | number` and had no null
  branch at all, so a nullable column added later would have sorted `undefined`
  lexically. `useClientSort` carries the nulls-last rule the server half carries.
*/

/**
 * One sortable key for the capability matrix. `name` and `held` are its two fixed
 * columns; every other key is one ROLE COLUMN of the grid.
 *
 * ⚠ A plain `string` rather than a union, because the role columns are not known at
 * compile time — they are whatever `data.roles` names, which comes from
 * `packages/auth`'s own roster over the wire. The map is built from that same array
 * below, so a key that has no column cannot be produced by any header on screen.
 */
/**
 * ⚠ A module-level constant, not `[]` inline. A fresh empty array every render
 * would change `useClientSort`'s dependency identity on every render while the
 * request is still in flight, re-running its sort for nothing.
 */
const EMPTY_CAPABILITIES: readonly MatrixCapability[] = []

type CapabilitySortKey = string

/** `role:PSD_USER` — namespaced so a role can never collide with `name` or `held`. */
function roleSortKey(role: string): CapabilitySortKey {
  return `role:${role}`
}

export function AccessControlMatrixPage() {
  const [roleDraft, setRoleDraft] = useState('')
  const [agencyDraft, setAgencyDraft] = useState('')
  const [role, setRole] = useState('')
  const [agencyCode, setAgencyCode] = useState('')

  const accountSort = useSort<AccountSortKey>()
  const findingSort = useSort<FindingSortKey>()

  /**
   * ★★ THE TWO OFFSETS THE FETCH IS KEYED ON — and they are held HERE because the
   * alternative does not typecheck, which is worth stating rather than leaving as a
   * puzzle for the next reader.
   *
   * ⚠ The obvious simplification is to key `useApi` on `accountPager.offset`
   * directly and delete this state. It cannot be done on THIS screen: both page
   * sizes and both totals come out of the RESPONSE (Wave 8 constraint 3 — no
   * `PAGE_SIZE` constant on the client), so `useOffsetPager` has to be constructed
   * from `matrix.data` and cannot be declared before the fetch that produces it.
   * The cycle is real, and the same shape `ExclusionListPage` and five sibling
   * screens carry: the hook owns the page NUMBER, this component owns the offset it
   * FETCHES with, and they are kept in step in exactly one place.
   *
   * ⚠ **What that costs, and what closed it — 2026-08-20.** "One place" used to be
   * an effect that ran AFTER the fetch, so a filter or sort change off page one
   * re-ran the fetcher at the STALE offset before the reset landed: one wasted
   * whole-estate `analyseGrants` pass plus two queries, on every filter or sort
   * change, on the one surface in this product where a page request IS an
   * estate-wide analysis. `useApi`'s `cancelled` guard meant the stale answer could
   * never RENDER, so nothing was corrupt — it was waste, and it was invisible to a
   * DOM suite asserting `state.calls.at(-1)`, because the last call cannot see an
   * intervening one. Every filter and sort change now resets BOTH the pager and this
   * offset IN THE SAME HANDLER, so the new predicate and offset 0 arrive in one
   * batched render and the fetcher runs ONCE; the effects below remain for the
   * pager's own movements (Next, Last, and the clamp when a population shrinks),
   * where there is no earlier point to write the offset from.
   */
  const [accountOffset, setAccountOffset] = useState(0)
  const [findingOffset, setFindingOffset] = useState(0)

  const matrix = useApi(
    () =>
      api.accessControlMatrix({
        ...(role.length > 0 && { role }),
        ...(agencyCode.length > 0 && { agencyCode }),
        offset: accountOffset,
        /*
         * ⚠ `sort` and `dir` TOGETHER or NEITHER — the server refuses a direction
         * with no sort key (`directionWithoutSortMessage`), because a bare
         * `?dir=desc` used to serve the LAST twenty emails as page one while the
         * response's own note called it the default ascending order. `useSort` makes
         * that structurally impossible here — its third click returns to
         * `{ column: null, dir: 'asc' }`, both halves at once — and
         * `AccessControlMatrixPage.dom.test.tsx` asserts that EVERY request this
         * screen sends, on first render and after a filter reset, carries the pair or
         * neither half of it.
         */
        ...(accountSort.sort.column !== null && { sort: accountSort.sort.column, dir: accountSort.sort.dir }),
        findingOffset,
        ...(findingSort.sort.column !== null && {
          findingSort: findingSort.sort.column,
          findingDir: findingSort.sort.dir,
        }),
      }),
    [
      role,
      agencyCode,
      accountOffset,
      accountSort.sort.column,
      accountSort.sort.dir,
      findingOffset,
      findingSort.sort.column,
      findingSort.sort.dir,
    ],
  )

  const data = matrix.data
  const roles = data?.roles ?? []

  /**
   * ★★ THE PAGE SIZE COMES FROM THE SERVER, never from a constant here.
   *
   * ⚠ Wave 8's global constraint 3 — never restate a server rule on the client. The
   * gateway derives both page sizes from a measured byte budget; a `PAGE_SIZE` in
   * this file would be a second copy of that arithmetic, and the copy that drifts
   * is always the one that looks derived. `1` while the first request is in flight
   * keeps the page arithmetic from dividing by zero, and the controls are disabled
   * then anyway.
   */
  const accountPageSize = data?.paging.accounts.limit ?? 1
  const findingPageSize = data?.paging.findings.limit ?? 1
  const accountPager = useOffsetPager(accountPageSize, data?.paging.accounts.total)
  const findingPager = useOffsetPager(findingPageSize, data?.paging.findings.total)

  // ⚠ The pager owns the page NUMBER; this component owns the offset it FETCHES
  // with. These two effects carry the pager's OWN movements — Next, Last, and the
  // clamp `useOffsetPager` applies when a population shrinks under a walk — none of
  // which has an earlier point to write the offset from. A filter or sort change
  // does, and takes it (see `resetBothWalks` below).
  useEffect(() => {
    if (accountPager.offset !== accountOffset) setAccountOffset(accountPager.offset)
  }, [accountPager.offset, accountOffset])
  useEffect(() => {
    if (findingPager.offset !== findingOffset) setFindingOffset(findingPager.offset)
  }, [findingPager.offset, findingOffset])

  // ⚠ A FILTER change is a different population on BOTH axes. Page three of the
  // unfiltered estate is not page three of the filtered one, and leaving either
  // offset alone shows an empty table that reads as "no results" when there are
  // plenty. Called from the submit and clear HANDLERS rather than from an effect
  // watching `role` — see above for what the effect cost.
  const resetBothWalks = () => {
    accountPager.reset()
    findingPager.reset()
    // ⚠ BOTH halves, in the same event. Resetting only the pager would leave this
    // component's fetch offset stale for exactly one render — which IS the wasted
    // request the effect used to cause.
    setAccountOffset(0)
    setFindingOffset(0)
  }
  // ⚠ ...and a SORT change is a new page one on the axis that changed, ONLY.
  // Re-ordering the findings must not throw the account walk back to page one.
  const onAccountSort = (column: AccountSortKey) => {
    accountSort.onSort(column)
    accountPager.reset()
    setAccountOffset(0)
  }
  const onFindingSort = (column: FindingSortKey) => {
    findingSort.onSort(column)
    findingPager.reset()
    setFindingOffset(0)
  }


  /**
   * ★★★ EVERY COLUMN OF THE MATRIX SORTS, INCLUDING THE NINE ROLE COLUMNS — added
   * 2026-08-24 by the sortable-header sweep, and they are the columns that carry the
   * question this screen exists to answer.
   *
   * A role column's cell is a tick: does this role hold this capability. Sorting on
   * it groups the capabilities that role HOLDS at one end of a 38-row grid, which is
   * *"what can a PSD user actually do"* — the question an access review asks, and
   * the one the two sortable columns beside it (capability name, and how many roles
   * hold it) cannot answer for any particular role.
   *
   * ⚠ Descending puts the HELD ones first. `useClientSort` sorts a boolean
   * false-first ascending, so the first click answers "what can this role NOT do"
   * and the second answers "what can it do". Both are real questions and neither
   * is wrong; it is stated here because it is the opposite of what a reader
   * expecting "sort by this role" might assume from the arrow alone.
   */
  const capabilityColumns: ClientSortColumns<MatrixCapability, CapabilitySortKey> = useMemo(() => {
    const columns: Record<CapabilitySortKey, ClientSortColumn<MatrixCapability>> = {
      name: { type: 'text', valueOf: (capability) => capability.name },
      held: { type: 'number', valueOf: (capability) => capability.heldBy.length },
    }
    for (const entry of roles) {
      columns[roleSortKey(entry.role)] = {
        type: 'boolean',
        valueOf: (capability) => capability.heldBy.includes(entry.role),
      }
    }
    return columns
  }, [roles])

  /**
   * The labels the live region speaks.
   *
   * ⚠ A role column's label is the FULL role name, not the abbreviation the header
   * shows. The header drops the `_USER` suffix to keep nine columns on screen; a
   * screen-reader user hearing "sorted by MMBA" would be given the abbreviation and
   * nothing to resolve it against.
   */
  const capabilityLabels: Readonly<Record<CapabilitySortKey, string>> = useMemo(() => {
    const labels: Record<CapabilitySortKey, string> = {
      name: 'Capability',
      held: 'Roles',
    }
    for (const entry of roles) labels[roleSortKey(entry.role)] = entry.label
    return labels
  }, [roles])

  const capabilitySort = useClientSort(data?.capabilities ?? EMPTY_CAPABILITIES, capabilityColumns)

  const capabilities = capabilitySort.rows

  /*
    ★★ NO CLIENT COMPARATOR ON THESE TWO — the rows arrive in the order the SERVER
    was asked for, and `accountSort`/`findingSort` reach it through the query above.

    ⚠ Re-sorting them here would be actively wrong, not merely redundant: these are
    a PAGE of the estate, so re-ordering the twenty rows in hand answers "which of
    these twenty holds the most authority" while looking exactly like an answer to
    "who on the estate holds the most authority". `useSort.ts` opens with that
    defect and it is the reason both sorts moved to the gateway.
  */
  const accounts = data?.accounts ?? []
  const findings = data?.analysis.findings ?? []

  const filtered = role.length > 0 || agencyCode.length > 0

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Access control matrix</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
          What every <strong>role</strong> may do, what authority every <strong>account</strong> holds, and what a
          review of the two together turns up. PSD-only, and <strong>read-only</strong>:
          access is granted in the account records, never from this screen.
        </p>
      </div>

      {/*
        ⚠ THE TWO FILTERS THIS SYSTEM CANNOT HONOUR, with the SERVER's own reasons.
        The story asks for six filter axes; four are real here. Saying so up front
        is the difference between a stated limitation and an officer discovering a
        400 by trying it.
      */}
      {data !== null && data !== undefined && (
        <SgdsAlert show variant="info" title="Two of the filters this report cannot offer, and why">
          <ul className="list-disc pl-5 m-0 flex flex-col gap-2">
            {Object.entries(data.filtersNotAvailable).map(([name, reason]) => (
              <li key={name}>
                <strong>{name}</strong>: {plainProse(reason)}
              </li>
            ))}
          </ul>
        </SgdsAlert>
      )}

      <div className="pt-card pt-no-print">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            setRole(roleDraft.trim())
            setAgencyCode(agencyDraft.trim())
            // ⚠ In the SAME handler as the filter, so the new predicate and offset 0
            // reach the fetcher in one batched render instead of one request at the
            // old offset followed by the real one.
            resetBothWalks()
          }}
        >
          <label className="flex flex-col gap-1 text-sm min-w-[14rem]">
            <span className="font-semibold">Role</span>
            <input
              type="text"
              className="pt-native-date"
              value={roleDraft}
              onChange={(e) => setRoleDraft(e.target.value)}
              placeholder="e.g. PAYMENT_USER"
            />
            <span className="text-xs text-[var(--sgds-body-color-subtle)]">
              A role this system defines. An unknown one is refused rather than answered with an empty matrix.
            </span>
          </label>
          <label className="flex flex-col gap-1 text-sm min-w-[14rem]">
            <span className="font-semibold">Account&rsquo;s own agency</span>
            <input
              type="text"
              className="pt-native-date"
              value={agencyDraft}
              onChange={(e) => setAgencyDraft(e.target.value)}
              placeholder="e.g. SGH"
            />
            <span className="text-xs text-[var(--sgds-body-color-subtle)]">
              Filters the agency the ACCOUNT belongs to, not the agency a grant covers.
            </span>
          </label>
          <SgdsButton type="submit" variant="primary">
            Apply
          </SgdsButton>
          {filtered && (
            <SgdsButton
              type="button"
              variant="outline"
              onClick={() => {
                setRoleDraft('')
                setAgencyDraft('')
                setRole('')
                setAgencyCode('')
                resetBothWalks()
              }}
            >
              Clear filters
            </SgdsButton>
          )}
        </form>
        <div className="flex flex-wrap gap-3 mt-4">
          {/*
            ⚠ THREE downloads, one per axis, because they are three different
            documents. A single link would have to pick one silently.
          */}
          {(['capability', 'user', 'findings'] as const).map((axis) => (
            <SgdsButton
              key={axis}
              variant="outline"
              type="button"
              onClick={() => {
                window.location.href = api.accessControlMatrixUrl('pdf', axis)
              }}
            >
              Download {axis} axis (PDF)
            </SgdsButton>
          ))}
        </div>
      </div>

      {matrix.error !== null ? (
        <ErrorCard title="Could not load the access control matrix" message={matrix.error} onRetry={matrix.reload} />
      ) : matrix.initialLoading ? (
        <LoadingCard label="Reading every role, account and grant" rows={6} />
      ) : data === null || data === undefined ? (
        /*
          ★★ WAVE 24 — an empty state must say WHICH of the two it is.
          It read: "Nothing to show — The matrix returned no data at all."
          That sentence is true of a broken deployment, of an estate with no
          accounts, and of a screen that was never finished, and it distinguishes
          none of them. This estate's rule is explicit: distinguish "not built"
          from "no data", and an unbuilt surface must say no action will fill it.
          Here the honest answer is the third thing: the route ANSWERED, and
          answered with nothing — which on this product means the directory the
          matrix reads has no accounts visible to this session.
        */
        <EmptyCard
          title="No accounts are visible to you"
          message={
            'The matrix was read successfully and came back with no accounts, no roles and no grants. That is ' +
            'not a failure and it is not an unbuilt screen: it means the account directory holds nothing your ' +
            'own scope can see. An account administrator in CAM can confirm what your role is entitled to read.'
          }
        />
      ) : (
        <>
          {/* ══ THE FINDINGS, FIRST — they are why an officer opens this page ══ */}
          <section className="flex flex-col gap-3">
            <h2 className="text-2xl">Review findings</h2>
            <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
              Each of these is a FACT about authority, with the evidence it was decided from.{' '}
              <strong>A finding is not automatically a defect</strong>: a central role legitimately holds
              grants over other agencies, and an administrative account legitimately holds no bill grant. What the
              matrix can do is put the pair in front of somebody who can decide.
            </p>

            {/*
              ★★ THE TALLIES, RENDERED EVEN AT ZERO. Three of the five detectors
              find nothing on a healthy estate; a screen showing only findings could
              not be told apart from one whose detectors never ran.
            */}
            <div className="pt-card">
              <p className="m-0 mb-3">
                Examined <strong>{data.analysis.accountsExamined}</strong> account(s) and{' '}
                <strong>{data.analysis.grantsExamined}</strong> grant(s) against{' '}
                <strong>{data.analysis.detectors.length}</strong> detector(s){' '}
                {/*
                  ★★ THE SENTENCE THAT STOPS A PAGED ZERO BEING READ AS AN ESTATE-WIDE
                  ONE. These tallies are the WHOLE (filtered) estate on every page —
                  the gateway computes them from one analysis pass and pages only the
                  rows. Without this said in words, "found 0" on page one of seventeen
                  reads as "the estate is clean", which is the exact silence the
                  tallies exist to refuse.
                */}
                <strong>across the whole estate</strong>, not only the{' '}
                {data.paging.findings.returned} finding(s) listed below.
              </p>
              <ul className="list-none p-0 m-0 flex flex-col gap-1 text-sm">
                {data.analysis.detectors.map((detector) => (
                  <li key={detector.kind} className="flex flex-wrap gap-2">
                    <strong title={detector.kind}>{plainCode(detector.kind)}</strong>
                    {/*
                      ★★ WAVE 9 — three states, not two. A detector that could NOT
                      RUN must not render as one that ran and found nothing: the
                      whole argument for showing these tallies at zero is that
                      "found 0" and silence are different claims, and "found 0"
                      and "could not look" are different claims by exactly the
                      same reasoning one level down. The reason comes from the
                      SERVER; this screen decides no detector's status.
                    */}
                    {detector.unavailable !== undefined ? (
                      <span>
                        <strong>did not run.</strong> {plainProse(detector.unavailable)}
                      </span>
                    ) : (
                      <>
                        <span className="text-[var(--sgds-body-color-subtle)]">
                          examined {detector.examined} {detector.unit}(s),
                        </span>
                        {detector.found === 0 ? (
                          <span>
                            found <strong>0</strong>: this detector ran and found nothing.
                          </span>
                        ) : (
                          <span>
                            found <strong>{detector.found}</strong>.
                          </span>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {findings.length === 0 ? (
              data.paging.findings.total === 0 ? (
                <EmptyCard
                  title="No findings"
                  message="Every detector ran and found nothing: the tallies above are the evidence of that, and this is not an empty screen waiting for data."
                />
              ) : (
                /*
                  ⚠ A DIFFERENT EMPTY STATE, and the difference matters. An empty page
                  of a non-empty axis is not "no findings"; it is an offset past the
                  end, usually because the population shrank under the walk. Saying
                  "No findings" here would report a clean estate that has
                  `paging.findings.total` findings in it.
                */
                <EmptyCard
                  title="No findings on this page"
                  message={`This estate has ${data.paging.findings.total} finding(s): this page of the findings axis is past the last one. Use First page below.`}
                />
              )
            ) : (
              <div className="pt-card p-0 overflow-hidden">
                <div className="pt-table-wrap">
                  <table className="pt-table">
                    <caption className="sr-only">
                      Access-control findings on this estate, one page of the findings axis at a time: every ATTENTION finding sorts to the front
                    </caption>
                    <thead>
                      <tr>
                        <SortableTh label="Severity" sortKey="severity" sort={findingSort.sort} onSort={onFindingSort} />
                        <SortableTh label="Finding" sortKey="kind" sort={findingSort.sort} onSort={onFindingSort} />
                        <SortableTh label="Account" sortKey="email" sort={findingSort.sort} onSort={onFindingSort} />
                        <SortableTh label="Role" sortKey="role" sort={findingSort.sort} onSort={onFindingSort} />
                        <th
                          scope="col"
                          data-unsortable="assembled prose and the evidence behind it, several sentences per finding rather than one value — Severity and Finding beside it are the axes this table is ordered by"
                        >
                          What is true, and the evidence
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {findings.map((finding, position) => (
                        <tr key={`${finding.userId}-${finding.kind}-${position}`}>
                          <td>
                            <CategoryBadge kind="severity" value={finding.severity} label={finding.severity === 'ATTENTION' ? 'Attention' : 'Review'} />
                          </td>
                          <td>
                            <CodeBadge value={finding.kind} />
                          </td>
                          <td className="pt-mono text-xs">{finding.email}</td>
                          <td><RoleBadge role={finding.role} /></td>
                          <td className="max-w-[48ch]">
                            <p className="m-0">{plainProse(finding.statement)}</p>
                            <p className="m-0 mt-2 text-xs text-[var(--sgds-body-color-subtle)]">
                              <strong>Evidence:</strong> {plainProse(finding.evidence)}
                            </p>
                            <p className="m-0 mt-1 text-xs text-[var(--sgds-body-color-subtle)]">
                              <strong>Why it matters:</strong> {plainProse(finding.tracesTo)}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/*
                  ★★ THE FINDINGS AXIS'S OWN PAGER. Separate from the account pager
                  because the gateway pages the two axes independently — and that is
                  what puts every ATTENTION finding on the WHOLE estate on page one of
                  this table, whatever page the account list is showing.
                */}
                <Pagination
                  pager={findingPager}
                  label="findings"
                  total={data.paging.findings.total}
                  shown={findings.length}
                />
              </div>
            )}
          </section>

          {/* ══ THE USER AXIS ══ */}
          <section className="flex flex-col gap-3">
            <h2 className="text-2xl">Authority held by each account</h2>
            <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
              Authority over a bill is a property of a <strong>grant</strong>, never of a role: two officers
              holding the same role can hold different grants. This is the axis that shows it.{' '}
              {/*
                ⚠ The count comes from the SERVER's `paging` block, never from
                `accounts.length` — that would be the page, described as the estate.
              */}
              <strong>{data.paging.accounts.total}</strong> account(s) on this estate,{' '}
              {data.paging.accounts.returned} on this page.
            </p>
            <div className="pt-card p-0 overflow-hidden">
              <div className="pt-table-wrap">
                <table className="pt-table">
                  <caption className="sr-only">
                    Accounts on this estate and the grants each one holds, one page at a time
                  </caption>
                  <thead>
                    <tr>
                      <SortableTh label="Account" sortKey="email" sort={accountSort.sort} onSort={onAccountSort} />
                      <SortableTh label="Role" sortKey="role" sort={accountSort.sort} onSort={onAccountSort} />
                      <SortableTh label="Own agency" sortKey="agencyCode" sort={accountSort.sort} onSort={onAccountSort} />
                      <SortableTh label="Active" sortKey="active" sort={accountSort.sort} onSort={onAccountSort} />
                      <SortableTh label="Grants" sortKey="grants" sort={accountSort.sort} onSort={onAccountSort} />
                      <th
                        scope="col"
                        data-unsortable="a nested list of every grant on the account, not one value — the Grants column beside it is the sortable count of exactly these"
                      >
                        Each grant
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.userId}>
                        <td className="pt-mono text-xs">{account.email}</td>
                        <td><RoleBadge role={account.role} /></td>
                        <td>
                          {account.agencyCode}{' '}
                          <span className="text-xs text-[var(--sgds-body-color-subtle)]">({plainCode(account.agencyKind)})</span>
                        </td>
                        <td>
                          {account.isActive ? <StateBadge value="ACTIVE" /> : <StateBadge value="DISABLED" />}
                        </td>
                        <td className="pt-num">{account.grants.length}</td>
                        <td>
                          {account.grants.length === 0 ? (
                            // ⚠ Said in words. A blank cell would read as "not
                            // applicable" rather than "every bill action is refused".
                            <span className="text-[var(--sgds-body-color-subtle)]">
                              none: every bill action is refused for this account
                            </span>
                          ) : (
                            <ul className="list-none p-0 m-0 flex flex-col gap-1 text-xs">
                              {account.grants.map((grant, position) => (
                                <li key={`${grant.agencyCode}-${grant.billType}-${grant.approvalLevel}-${position}`}>
                                  <strong>{grant.agencyCode}</strong> &middot; {plainCode(grant.billType)} &middot; level{' '}
                                  {grant.approvalLevel} &middot;{' '}
                                  {grant.approvalLimit === null ? (
                                    // ⚠ NOT blank, and not a large number: "no
                                    // ceiling" is a different statement from "a high
                                    // ceiling", and it is the one that matters here.
                                    <span className="font-semibold">no ceiling</span>
                                  ) : (
                                    <>ceiling {formatCents(grant.approvalLimit)}</>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                pager={accountPager}
                label="accounts"
                total={data.paging.accounts.total}
                shown={accounts.length}
              />
            </div>
          </section>

          {/* ══ THE CAPABILITY AXIS ══ */}
          <section className="flex flex-col gap-3">
            <h2 className="text-2xl">What each role may do</h2>
            <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
              <strong>{data.capabilities.length}</strong> capability(ies) &times; <strong>{roles.length}</strong>{' '}
              role(s). Every cell is the live answer from <code>packages/auth</code>, enumerated from the package
              itself, not a list kept here. A capability added anywhere in the authorisation model appears in
              this table with nothing to update.
              {filtered && (
                <>
                  {' '}
                  <strong>This table is not narrowed by the filters above</strong>: a capability is a property of a
                  role, so an account filter cannot change it.
                </>
              )}
            </p>
            {/*
              ⚠ `busy={false}`, unlike the two paged tables on this screen. Those
              re-order on the SERVER and must stay silent between the click and the
              response; this one is synchronous, so by the time this renders the rows
              ARE in the new order and there is no in-flight window to wait through.
            */}
            <SortAnnouncement
              sort={capabilitySort.sort}
              interacted={capabilitySort.interacted}
              busy={false}
              columnLabels={capabilityLabels}
              rowCount={capabilities.length}
            />
            <div className="pt-card p-0 overflow-hidden">
              <div className="pt-table-wrap">
                <table className="pt-table">
                  <caption className="sr-only">
                    What each role may do: one row per capability in the authorisation model, one column per role, not narrowed by any filter
                  </caption>
                  <thead>
                    <tr>
                      <SortableTh label="Capability" sortKey="name" sort={capabilitySort.sort} onSort={capabilitySort.onSort} />
                      <SortableTh label="Roles" sortKey="held" sort={capabilitySort.sort} onSort={capabilitySort.onSort} align="right" />
                      {/*
                        ⚠ The `title` carries the full role name and the visible text
                        drops the `_USER` suffix — nine columns have to fit. That was
                        already true when these were plain `<th>`s; what is new is that
                        `SortableTh` puts a real button inside the header, so the
                        abbreviation is now also the button's accessible name. The live
                        region speaks the FULL name (`capabilityLabels`), which is where
                        a screen-reader user gets the unabbreviated form.
                      */}
                      {roles.map((entry) => (
                        <SortableTh
                          key={entry.role}
                          label={humaniseCode(entry.role.replace(/_USER$/, ''))}
                          title={entry.label}
                          sortKey={roleSortKey(entry.role)}
                          sort={capabilitySort.sort}
                          onSort={capabilitySort.onSort}
                        />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {capabilities.map((capability) => (
                      <tr key={capability.name}>
                        <td className="text-sm" title={capability.name}>{plainCode(capability.name)}</td>
                        <td className="pt-num">{capability.heldBy.length}</td>
                        {roles.map((entry) => (
                          <td key={entry.role}>
                            {capability.heldBy.includes(entry.role) ? (
                              <CategoryBadge kind="grant" value="Yes" label="Yes" />
                            ) : (
                              // ⚠ "No" in words, not an empty cell: a blank in a
                              // permission grid is ambiguous between "not held" and
                              // "not answered", and this grid is read to decide access.
                              <CategoryBadge kind="grant" value="No" label="No" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* ══ THE NOTES THE SERVER SENT, VERBATIM ══ */}
          <section className="flex flex-col gap-3">
            <h2 className="text-2xl">What this matrix does and does not cover</h2>
            {/*
              ⚠ Rendered VERBATIM from the response. These sentences state what the
              numbers exclude, and a screen that paraphrased them would be a second
              copy of the caveats — the thing that drifts first.
            */}
            <ul className="pt-card list-disc pl-8 m-0 flex flex-col gap-2 text-sm">
              {data.notes.map((note, position) => (
                <li key={position}>{plainProse(note)}</li>
              ))}
            </ul>
            <p className="text-xs text-[var(--sgds-body-color-subtle)]">Read at {data.generatedAt}.</p>
          </section>
        </>
      )}
    </div>
  )
}
